import { useState, useRef } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Crop } from 'react-image-crop';
import { PhotoPreview } from "./PhotoPreview";
import { PhotoUploadButton } from "./PhotoUploadButton";
import { PhotoRequirements } from "./PhotoRequirements";
import { ImageCropDialog } from "./ImageCropDialog";

interface PhotoUploadProps {
  profile: any;
  avatarUrl: string | null;
  setAvatarUrl: (url: string) => void;
}

export const PhotoUpload = ({ profile, avatarUrl, setAvatarUrl }: PhotoUploadProps) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [showCropDialog, setShowCropDialog] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 100,
    height: 100,
    x: 0,
    y: 0
  });
  
  const imageRef = useRef<HTMLImageElement | null>(null);

  const handleImageCrop = async () => {
    if (!imageRef.current || !crop) return;

    const canvas = document.createElement('canvas');
    const scaleX = imageRef.current.naturalWidth / imageRef.current.width;
    const scaleY = imageRef.current.naturalHeight / imageRef.current.height;
    
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(
      imageRef.current,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    canvas.toBlob(async (blob) => {
      if (!blob) return;

      try {
        setUploading(true);
        const file = new File([blob], 'cropped-avatar.jpg', { type: 'image/jpeg' });
        const fileExt = 'jpg';
        const filePath = `${profile.id}/${Math.random()}.${fileExt}`;

        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, file);

        if (uploadError) {
          throw uploadError;
        }

        const { data: { publicUrl } } = supabase.storage
          .from('avatars')
          .getPublicUrl(filePath);

        const { error: updateError } = await supabase
          .from('profiles')
          .update({ avatar_url: publicUrl })
          .eq('id', profile.id);

        if (updateError) {
          throw updateError;
        }

        setAvatarUrl(publicUrl);
        setShowCropDialog(false);
        setCropImage(null);
        toast.success(t('profile.personalInfo.photoUpdated'));
      } catch (error) {
        toast.error(t('profile.personalInfo.photoError'));
        console.error('Error:', error);
      } finally {
        setUploading(false);
      }
    }, 'image/jpeg', 0.95);
  };

  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        throw new Error('You must select an image to upload.');
      }

      const file = event.target.files[0];
      const reader = new FileReader();
      
      reader.onload = () => {
        setCropImage(reader.result as string);
        setShowCropDialog(true);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error(t('profile.personalInfo.photoError'));
      console.error('Error:', error);
    }
  };

  return (
    <>
      <div className="flex flex-col items-center space-y-4">
        <PhotoPreview 
          avatarUrl={avatarUrl} 
          profile={profile} 
          onClick={() => document.getElementById('avatar-upload')?.click()} 
        />
        <PhotoUploadButton 
          uploading={uploading} 
          onChange={handleAvatarUpload} 
        />
      </div>

      <PhotoRequirements />

      <ImageCropDialog
        open={showCropDialog}
        onOpenChange={setShowCropDialog}
        cropImage={cropImage}
        crop={crop}
        onCropChange={setCrop}
        imageRef={imageRef}
        onSave={handleImageCrop}
        uploading={uploading}
      />
    </>
  );
};